/**
 * TanStack Router setup — code-based routes.
 *
 * Estructura:
 *  - /              → HomePage (workspace selector)
 *  - /c/$slug       → WorkspacePage (dashboard del cliente)
 *  - /c/$slug/inbox, /metricas, /alertas, /cuentas, /skills, /publicaciones
 *                   → páginas scoped al cliente
 *  - /sandbox, /audit, /admin
 *                   → herramientas globales cross-cliente
 *
 * Las rutas legacy (/inbox, /metrics, /alerts, /accounts, /skills) ya no
 * existen — al perder el contexto del workspace dejaron de tener sentido.
 * Quedan rutas globales solo donde el cross-cliente es la feature principal
 * (Sandbox de comparación de skills, Audit log con cadena verificable cross,
 *  Admin con kill-switch maestro).
 */
import { createRootRoute, createRoute, createRouter, redirect, Outlet } from '@tanstack/react-router'
import { Shell } from './components/layout/shell'
import { HomePage } from './pages/home'
import { WorkspacePage } from './pages/workspace'
import { InboxPage } from './pages/inbox'
import { ConversationPage } from './pages/conversation'
import { PublicacionesPage } from './pages/publicaciones'
import { SandboxListPage } from './pages/sandbox-list'
import { SandboxNewPage } from './pages/sandbox-new'
import { SandboxRunPage } from './pages/sandbox-run'
import { SandboxChatPage } from './pages/sandbox-chat'
import { SkillsPage } from './pages/skills'
import { MetricsPage } from './pages/metrics'
import { MonitoringPage } from './pages/monitoring'
import { AlertsPage } from './pages/alerts'
import { AuditPage } from './pages/audit'
import { AccountsPage } from './pages/accounts'
import { AdminPage } from './pages/admin'
import { PlatformsPage } from './pages/platforms'
import { LoginPage } from './pages/login'
import { NotFoundPage } from './pages/not-found'

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

// Layout route — todas las rutas autenticadas viven dentro del Shell.
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: () => (
    <Shell>
      <Outlet />
    </Shell>
  ),
})

const indexRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: HomePage,
})

// ─── Workspace scoped ─────────────────────────────────────────────────

const workspaceRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug',
  component: WorkspacePage,
})

const workspaceInboxRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/inbox',
  component: InboxPage,
})

const workspaceConversationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/inbox/$conversationId',
  component: ConversationPage,
})

const workspacePublicacionesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/publicaciones',
  component: PublicacionesPage,
})

const workspaceMetricsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/metricas',
  component: MetricsPage,
})

const workspaceMonitoringRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/monitoreo',
  component: MonitoringPage,
})

const workspaceAlertsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/alertas',
  component: AlertsPage,
})

const workspaceAccountsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/cuentas',
  component: AccountsPage,
})

const workspaceSkillsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/skills',
  component: SkillsPage,
})

const workspaceSimulatorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/c/$clientSlug/simulador',
  component: SandboxChatPage,
})

// ─── Global tools ─────────────────────────────────────────────────────

const sandboxListRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/sandbox',
  component: SandboxListPage,
})

const sandboxNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/sandbox/new',
  component: SandboxNewPage,
})

const sandboxRunRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/sandbox/$runId',
  component: SandboxRunPage,
})

const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/audit',
  component: AuditPage,
})

const adminRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/admin',
  component: AdminPage,
})

const platformsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/admin/plataformas',
  component: PlatformsPage,
})

// ─── Legacy redirects ─────────────────────────────────────────────────
// Rutas viejas globales — redirigen a home para no romper bookmarks.

const legacyInboxRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/inbox',
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

const legacyMetricsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/metrics',
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

const legacyAlertsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/alerts',
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

const legacyAccountsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/accounts',
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

const legacySkillsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/skills',
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  shellRoute.addChildren([
    indexRoute,
    workspaceRoute,
    workspaceInboxRoute,
    workspaceConversationRoute,
    workspacePublicacionesRoute,
    workspaceMetricsRoute,
    workspaceMonitoringRoute,
    workspaceAlertsRoute,
    workspaceAccountsRoute,
    workspaceSkillsRoute,
    workspaceSimulatorRoute,
    sandboxListRoute,
    sandboxNewRoute,
    sandboxRunRoute,
    auditRoute,
    adminRoute,
    platformsRoute,
    legacyInboxRoute,
    legacyMetricsRoute,
    legacyAlertsRoute,
    legacyAccountsRoute,
    legacySkillsRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
